import {
  formatCount,
  formatDeltaBadge,
  pickNumber,
  unwrapStats,
} from "./salesDashboard.utils";

const PIPELINE_STAGES = [
  { key: "NEW", stage: "New" },
  { key: "CONTACTED", stage: "Contacted" },
  { key: "FOLLOW_UP", stage: "Follow-up" },
  { key: "PROPOSAL_SENT", stage: "Proposal" },
  { key: "NEGOTIATION", stage: "Negotiation" },
  { key: "CLOSED_WON", stage: "Won" },
  { key: "CLOSED_LOST", stage: "Lost" },
];

const emptyDashboard = (isStatsLoading) => ({
  row1Kpis: [],
  pipelineData: null,
  leadSourcesData: null,
  weeklyLeadsData: null,
  topTreks: [],
  isStatsLoading,
});

const normalizeDailyLeads = (stats) => {
  const raw =
    stats?.new_leads_by_day ??
    stats?.newLeadsByDay ??
    stats?.daily_leads ??
    stats?.dailyLeads ??
    stats?.weekly_trend ??
    stats?.weeklyTrend;
  if (!Array.isArray(raw)) return [];
  return raw.map((item) => ({
    date: item?.date ?? item?.day ?? item?.label ?? "",
    count: Number(item?.count ?? item?.leads ?? item?.value ?? 0) || 0,
  }));
};

export default function mapCrmStatsDashboard(rawStats, isLoading) {
  if (isLoading) return emptyDashboard(true);

  const stats = unwrapStats(rawStats);
  if (!stats) return emptyDashboard(false);

  const totalLeads = pickNumber(stats, "total_leads", "totalLeads") ?? 0;
  const closedWon = pickNumber(stats, "closed_won", "closedWon", "leads_won") ?? 0;
  const conversionRate =
    pickNumber(stats, "conversion_rate", "conversionRate") ??
    (totalLeads > 0 ? (closedWon / totalLeads) * 100 : null);
  const overdueFollowups =
    pickNumber(stats, "overdue_followups_count", "overdue_followups", "followups_overdue") ?? 0;
  const dailyLeads = normalizeDailyLeads(stats);
  const weekTotal = dailyLeads.reduce((sum, item) => sum + item.count, 0);
  const peak = dailyLeads.reduce(
    (best, item) => (item.count > best.count ? item : best),
    dailyLeads[0] || { count: 0, date: "" }
  );
  const peakDate = peak.date ? new Date(peak.date) : null;
  const peakLabel =
    peakDate && !Number.isNaN(peakDate.getTime())
      ? `Peak: ${peak.count} on ${peakDate.toLocaleDateString("en-US", {
          weekday: "short",
          month: "short",
          day: "numeric",
        })}`
      : null;

  const sources = stats?.leads_by_source ?? stats?.leadsBySource ?? {};
  const sourceEntries = Object.entries(sources).filter(([, value]) => Number(value) > 0);
  const sourceTotal =
    pickNumber(stats, "total_leads", "totalLeads") ??
    sourceEntries.reduce((sum, [, value]) => sum + (Number(value) || 0), 0);
  const byStatus = stats?.leads_by_status ?? stats?.leadsByStatus ?? {};
  const pipeline = PIPELINE_STAGES.map(({ key, stage }) => ({
    key,
    stage,
    count: Number(byStatus?.[key] ?? 0) || 0,
  }));

  return {
    row1Kpis: [
      {
        label: "Total Leads",
        value: formatCount(totalLeads),
        badge: formatDeltaBadge(
          pickNumber(stats, "leads_this_week", "new_leads_this_week", "leads_added_this_week"),
          "this week"
        ),
        badgeTone: "green",
        iconTone: "violet",
      },
      {
        label: "Closed Won",
        value: formatCount(closedWon),
        badge: conversionRate != null ? `${Number(conversionRate).toFixed(1)}% conversion` : null,
        badgeTone: "green",
        iconTone: "green",
      },
      {
        label: "Active Leads",
        value: formatCount(pickNumber(stats, "active_leads", "activeLeads") ?? 0),
        badge: formatDeltaBadge(
          pickNumber(stats, "active_leads_this_month", "new_active_leads_this_month"),
          "this month"
        ),
        badgeTone: "purple",
        iconTone: "purple",
      },
      {
        label: "Quotations",
        value: formatCount(pickNumber(stats, "total_quotations", "totalQuotations") ?? 0),
        badge: formatDeltaBadge(
          pickNumber(stats, "quotations_this_month", "new_quotations_this_month"),
          "this month"
        ),
        badgeTone: "purple",
        iconTone: "amber",
      },
      {
        label: "Today Follow-Ups",
        value: formatCount(
          pickNumber(stats, "today_followups_count", "today_followups", "followups_today") ?? 0
        ),
        badge: overdueFollowups > 0 ? `${overdueFollowups} overdue` : null,
        badgeTone: "red",
        iconTone: "blue",
      },
      {
        label: "Avg Pax / Lead",
        value: formatCount(
          pickNumber(stats, "avg_pax_per_lead", "average_pax_per_lead", "avg_persons_per_lead") ?? 0
        ),
        badge: "avg persons",
        badgeTone: "neutral",
        iconTone: "slate",
      },
    ],
    pipelineData: {
      pipeline,
      max_count: Math.max(...pipeline.map((row) => row.count), 1),
    },
    leadSourcesData: {
      sources: sourceEntries.map(([key, value]) => {
        const count = Number(value) || 0;
        return {
          key: String(key).toLowerCase(),
          label: String(key).replace(/_/g, " "),
          count,
          percentage: sourceTotal > 0 ? (count / sourceTotal) * 100 : 0,
        };
      }),
      total_leads: sourceTotal,
    },
    weeklyLeadsData: {
      days: dailyLeads,
      summaryText: peakLabel ? `${weekTotal} leads this week · ${peakLabel}` : `${weekTotal} leads this week`,
    },
    topTreks: (stats.top_treks || stats.topTreks || []).slice(0, 5).map((trek) => ({
      name: trek.name || trek.trek_name || trek.trek || trek.label || "Unknown",
      count: Number(trek.count || trek.value || trek.leads_count || 0) || 0,
    })),
    isStatsLoading: false,
  };
}
